import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { ClosableModal } from '../../../../shared/modules/modal/closable-modal';
import {
  ConfirmationModalComponent,
} from '../../../../shared/components/confirmation-modal/confirmation-modal.component';

import {
  StudiesService,
  TransactionsService,
  SessionService,
  NotificationService,
  UtilsService } from '../../../../shared/services';
import { ModalService } from '../../../../shared/modules/modal/modal.service';

import { PaginationConstants, TranslationConstants } from '../../../../shared/constants';

import {
  Currency,
  StudyTransaction,
  Entity,
  AddTransactionsPayload,
  TransactionType,
  Message,
  StudyModalsPayload,
  Transaction } from '../../../../shared/models';

@Component({
  selector: 'add-transactions-modal',
  templateUrl: './add-transactions-modal.component.html',
})
export class AddTransactionsModalComponent extends ClosableModal implements OnInit, OnDestroy {
  private subscriptions = [];

  data: StudyModalsPayload;
  transactions: StudyTransaction[] = [];
  pendingSelectedTransactions: StudyTransaction[] = []; // needed for pagination and filtering
  sourceEntities: Entity[] = [];
  destinationEntities: Entity[] = [];
  transactionTypes: TransactionType[];
  selectedSourceEntityId: number;
  selectedDestinationEntityId: number;
  selectedTransactionTypeId = 0;
  sourceEntitiesLoading: boolean;
  destinationEntitiesLoading: boolean;
  pageSizes: number[];
  upeCurrency: Currency;

  currentPage = 1;
  totalItems: number;
  selectedPageSize = PaginationConstants.DEFAULT_PAGE_SIZE;

  responseErrors: string[] = [];

  constructor(
    protected modalService: ModalService,
    private _studiesService: StudiesService,
    private _transactionsService: TransactionsService,
    private _sessionService: SessionService,
    private _utilsService: UtilsService,
    private _notificationsService: NotificationService
  ) {
    super(modalService);
  }

  ngOnInit(): void {
    this.upeCurrency = this._sessionService.getContainerUpeCurrency();
    this.pageSizes = PaginationConstants.PAGE_SIZES;
    this.getAllTransactions();
    this.listenForErrorMessages();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  save(): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    if (this.pendingSelectedTransactions.length === 0) {
      this.closeAllModals();
      return;
    }

    const transactionsPayload = this.getPayloadForSave();
    this._studiesService.addTransactions(this.data.study.studyId, transactionsPayload).subscribe(() => {
      this.closeAllModals();
      this.success();
    });
  }

  // override
  close(): void {
    if (this.pendingSelectedTransactions.length > 0) {
      this.modalService.openModal({
        component: ConfirmationModalComponent,
        data: {
          title: 'Close Add Transactions',
          content: 'Are you sure you want to cancel your actions and close?',
        } as Message,
        success: () => {
          this.closeAllModals();
        },
      });
    } else {
      this.closeAllModals();
    }
  }

  selectedTransactionChange(transaction: StudyTransaction): void {
    if (transaction.selected) {
      this.pendingSelectedTransactions.push(transaction);
    } else {
      const pendingTransaction = this.pendingSelectedTransactions.find((studyTransaction) => {
        return studyTransaction.transactionId === transaction.transactionId;
      });

      if (!pendingTransaction) {
        return;
      }
      this.pendingSelectedTransactions = this.pendingSelectedTransactions.filter(pendingSelectedTransaction => pendingSelectedTransaction.transactionId !== transaction.transactionId);
    }
  }

  pageChanged(event: any): void {
    this.currentPage = event.page;
    this.getAllTransactions();
  }

  pageSizeChanged(): void {
    this.getAllTransactions();
  }

  removeFilter() {
    this.selectedSourceEntityId = null;
    this.selectedDestinationEntityId = null;
    this.selectedTransactionTypeId = 0;
    this.getAllTransactions();
  }

  applyFilter() {
    this.getAllTransactions();
  }

  getEntityCode(transaction: Transaction, isSource = true): string {
    return this._utilsService.getEntityCodeFromTransaction(transaction, isSource);
  }

  onSourceEntityChanged(): void {
    this.getDestinationEntities();
    const filterQueryString = `?${this.getFilterQueryString()}`;
    this.getTransactionTypes(filterQueryString);
  }

  onDestinationEntityChanged(): void {
    this.getSourceEntities();
    const filterQueryString = `?${this.getFilterQueryString()}`;
    this.getTransactionTypes(filterQueryString);
  }

  private getFilterQueryString(): string {
    let queryString;
    if (!!this.selectedSourceEntityId ||
      !!this.selectedDestinationEntityId ||
      +this.selectedTransactionTypeId !== 0) {
      const queryParameters = [];
      if (!!this.selectedSourceEntityId) {
        queryParameters.push(`sourceEntity=${this.selectedSourceEntityId}`);
      }
      if (!!this.selectedDestinationEntityId) {
        queryParameters.push(`destinationEntity=${this.selectedDestinationEntityId}`);
      }
      if (+this.selectedTransactionTypeId !== 0) {
        queryParameters.push(`transactionType=${this.selectedTransactionTypeId}`);
      }
      if (queryParameters.length > 0) {
        queryString = `${queryParameters.join('&')}`;
      }
    }
    return queryString;
  }

  private getPaginationQueryString(): string {
    const queryParameters = [];
    let queryString: string;
    if (!!this.selectedPageSize) {
      queryParameters.push(`length=${this.selectedPageSize}`);
    }
    if (!!this.currentPage) {
      queryParameters.push(`page=${this.currentPage}`);
    }
    if (queryParameters.length > 0) {
      queryString = `${queryParameters.join('&')}`;
    }
    return queryString;
  }

  private getAllTransactions(): void {
    const filterQueryString = this.getFilterQueryString();
    let query = `?includeUnassigned=1&${this.getPaginationQueryString()}`;
    let filtersQuery;
    if (!!filterQueryString) {
      query += `&${filterQueryString}`;
      filtersQuery = `?${filterQueryString}`;
    }

    this._studiesService.getTransactions(this.data.study.studyId, query).subscribe(({ data, count }) => {
      this.transactions = data.map((studyTransaction: StudyTransaction) => {
        studyTransaction.selected = !!studyTransaction.study ||
          !!this.pendingSelectedTransactions.find(pendingTransaction => studyTransaction.transactionId === pendingTransaction.transactionId);
        studyTransaction.disabled = !!studyTransaction.study;
        return studyTransaction;
      });
      this.totalItems = count;
      this.getSourceEntities(filtersQuery);
      this.getDestinationEntities(filtersQuery);
      this.getTransactionTypes(filtersQuery);
    });
  }

  private getPayloadForSave(): AddTransactionsPayload {
    const transactionsPayload = new AddTransactionsPayload();
    transactionsPayload.transactionIds = this.pendingSelectedTransactions.map((studyTransaction) => {
      return studyTransaction.transactionId;
    });

    return transactionsPayload;
  }

  private getSourceEntities(filterQueryString?: string): void {
    this.sourceEntitiesLoading = true;
    this._transactionsService.getSourceEntities(filterQueryString).subscribe((res) => {
      this.sourceEntities = res.result.legalEntities;
      this.sourceEntitiesLoading = false;

      // filter on destination selected
      if (!this.selectedDestinationEntityId) {
        return;
      }
      const primaryEntityId = this.data.study.primaryEntity.entityId;
      if (this.selectedDestinationEntityId && this.selectedDestinationEntityId !== primaryEntityId) {
        this.sourceEntities = this.sourceEntities.filter(entity => entity.entityId === primaryEntityId);
        this.selectedSourceEntityId = primaryEntityId;
      } else {
        this.sourceEntities = this.sourceEntities.filter(entity => entity.entityId !== primaryEntityId);
      }
    });
  }

  private getDestinationEntities(filterQueryString?: string): void {
    this.destinationEntitiesLoading = true;
    this._transactionsService.getDestinationEntities(filterQueryString).subscribe((res) => {
      this.destinationEntities = res.result.legalEntities;
      this.destinationEntitiesLoading = false;

      // filter on source selected
      if (!this.selectedSourceEntityId) {
        return;
      }
      const primaryEntityId = this.data.study.primaryEntity.entityId;
      if (this.selectedSourceEntityId && this.selectedSourceEntityId !== primaryEntityId) {
        this.destinationEntities = this.destinationEntities.filter(entity => entity.entityId === primaryEntityId);
        this.selectedDestinationEntityId = primaryEntityId;
      } else {
        this.destinationEntities = this.destinationEntities.filter(entity => entity.entityId !== primaryEntityId);
      }
    });
  }

  private getTransactionTypes(filterQueryString?: string): void {
    this._transactionsService.getTransactionTypesForContainer(filterQueryString).subscribe((res) => {
      this.transactionTypes = res.result.types;
    });
  }

  private listenForErrorMessages() {
    // this.subscriptions.push(
    //   this._notificationsService.notification$.subscribe((message) => {
    //     this.handleErrors(message);
    //   })
    // );
  }

  protected handleErrors(res: HttpErrorResponse): void {
    if (res.status === 400) {
      this.responseErrors.push(res.error.errors.global);
    } else {
      this.responseErrors.push(TranslationConstants.GLOBAL_SERVER_ERROR);
    }
  }
}
